import { EmployeeIdChoice } from '../../../components/EmployeeIdChoice';
import { RequirePermission } from '../components/NoAccess';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CursorPage, EmployeeDetail, Plant } from '@dru/types';
import { BRAND, BLOOD_GROUPS } from '@dru/types';
import {
  Alert, Button, Card, CardBody, CardHeader, Field, Input, SearchSelect, SelectField, TextField, useIdempotencyKey, STICKY_ACTIONS, cn,
  useToast,
} from '@dru/ui';
import { Camera, Trash2 } from 'lucide-react';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';
import { EmployeeAvatar, PhotoDialog, type PhotoShot } from '../components/EmployeePhoto';

/**
 * One worker, added by hand.
 *
 * The bulk path is the CSV import and it is the one most companies use; this
 * exists for the new joinee who starts on Monday and cannot wait for the next
 * upload. It asks for exactly the employee contract the import template does
 * (docs/38 D7) — the same eleven facts, the same rules — so a person added
 * here and one imported are the same shape of record.
 *
 * Only name, gender, date of birth and mobile are required, with the plant
 * when the company has more than one (flow audit 4.1, 20 Oct 2026); a medical
 * needs nothing else. Designation and nature of work are asked for, optional,
 * because the fitness certificate prints them. Job role, contractor company,
 * home address and the rest of the occupational profile are not asked for.
 * The API still accepts them, and records that have them keep them; the
 * worker's page lists them.
 *
 * Field errors come back from the server keyed by field name — the same Zod
 * schema that rejects a malformed mobile is the one the API enforces. The
 * checks below mirror it for the person typing; they do not replace it.
 */

interface Draft {
  personType: 'EMPLOYEE' | 'CANDIDATE';
  employeeCode: string;
  fullName: string;
  sex: string;
  dateOfBirth: string;
  mobile: string;
  email: string;
  bloodGroup: string;
  preferredLocale: string;

  plantId: string;
  employmentType: 'DIRECT' | 'CONTRACT';
  joinedOn: string;
  designation: string;
  natureOfWork: string;
}

const EMPTY: Draft = {
  personType: 'EMPLOYEE',
  employeeCode: '', fullName: '', sex: '', dateOfBirth: '', mobile: '', email: '', bloodGroup: '',
  preferredLocale: 'en',
  plantId: '',
  employmentType: 'DIRECT', joinedOn: '',
  designation: '', natureOfWork: '',
};

/** Blanks are absent, not empty strings — the API's schema rejects `""`. */
function payload(d: Draft) {
  const put = (v: string) => (v.trim() ? v.trim() : undefined);
  return {
    personType: d.personType,
    employeeCode: put(d.employeeCode),
    fullName: d.fullName.trim(),
    sex: put(d.sex),
    dateOfBirth: put(d.dateOfBirth),
    mobile: put(d.mobile),
    email: put(d.email),
    bloodGroup: put(d.bloodGroup),
    preferredLocale: d.preferredLocale,
    plantId: d.plantId,
    employmentType: d.employmentType,
    // A candidate has not joined; the hire writes the date.
    joinedOn: d.personType === 'EMPLOYEE' ? put(d.joinedOn) : undefined,
    designation: put(d.designation),
    natureOfWork: put(d.natureOfWork),
  };
}

function EmployeeNewPage() {
  return <EmployeeIdChoice><EmployeeNewForm /></EmployeeIdChoice>;
}

function EmployeeNewForm() {
  const nav = useNavigate();
  const [d, setD] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  // Held here until the person exists to attach it to: the record is
  // created first, then the photo is put on it, so a photo that fails to
  // upload leaves a worker without a picture rather than no worker.
  const [shot, setShot] = useState<PhotoShot | null>(null);
  const [choosingPhoto, setChoosingPhoto] = useState(false);

  const set = <K extends keyof Draft>(k: K) => (v: Draft[K]) => {
    setD((prev) => ({ ...prev, [k]: v }));
    setFieldErrors((prev) => { const { [k]: _, ...rest } = prev; return rest; });
  };
  const idem = useIdempotencyKey();

  // The calendar day where the person sits, not UTC's: at 02:00 IST the UTC
  // date is still yesterday, and a worker born today would be "in the future".
  const today = localDay(new Date());
  const yearAhead = localDay(new Date(Date.now() + 365 * 86_400_000));

  // Searched server-side rather than preloaded: a form that lists an entire
  // company's plants works until the company is large, and then quietly
  // stops offering the one somebody needs.
  const [plant, setPlant] = useState<Plant | null>(null);

  // Employment is optional (owner, 26 Oct 2026), but every employee belongs
  // to a plant. So the main plant the login can see is filled in for them
  // — the only one, for most companies — and stays changeable.
  useEffect(() => {
    let live = true;
    // Newest first from the API, so the last is the company's original —
    // its main plant, created at registration.
    api.get<CursorPage<Plant>>('/plants?limit=100').then((res) => {
      if (!live || res.items.length === 0) return;
      // The plant chosen in the header is where this person is added (owner,
      // 29 Oct 2026: no plant field on Add; it is edited later if needed).
      const viewing = api.getViewPlant();
      const first = res.items.find((x) => x.id === viewing) ?? res.items[res.items.length - 1]!;
      setPlant((cur) => cur ?? first);
      setD((prev) => (prev.plantId ? prev : { ...prev, plantId: first.id }));
    }).catch(() => { /* no plant: the form says so beside Add */ });
    return () => { live = false; };
  }, []);


  // Client-side, for the person typing; the server enforces the same rules
  // (CreateEmployeeSchema) and its messages land in the same places.
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    const code = d.employeeCode.trim();
    if (!code) e.employeeCode = 'Employee ID is required';
    else if (code.length > 40) e.employeeCode = 'Employee ID must be 40 characters or fewer';
    else if (code && !/^[A-Za-z0-9/_-]+$/.test(code)) e.employeeCode = 'Letters, digits, / - and _ only';
    if (d.fullName.trim().length < 2) e.fullName = d.fullName.trim() ? 'Full name must be at least 2 characters' : 'Full name is required';
    if (!d.sex) e.sex = 'Gender is required';
    if (!d.dateOfBirth) e.dateOfBirth = 'Date of birth is required';
    else if (Number.isNaN(new Date(d.dateOfBirth).getTime())) e.dateOfBirth = 'Enter a valid date of birth';
    else if (d.dateOfBirth > today) e.dateOfBirth = 'Date of birth cannot be in the future.';
    else if (Number(d.dateOfBirth.slice(0, 4)) < 1900) e.dateOfBirth = 'Enter a valid date of birth.';
    if (!d.mobile.trim()) e.mobile = 'Mobile number is required';
    else if (!/^[6-9]\d{9}$/.test(d.mobile.trim())) e.mobile = 'Enter a valid 10-digit mobile number';
    if (d.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email.trim())) e.email = 'Enter a valid email address';
    if (!d.plantId) e.plantId = 'Choose a plant';
    if (d.personType === 'EMPLOYEE' && d.joinedOn) {
      if (d.joinedOn > yearAhead) e.joinedOn = 'Joining date cannot be more than a year ahead.';
      else if (d.dateOfBirth && d.joinedOn <= d.dateOfBirth) e.joinedOn = 'Joining date must be after the date of birth.';
    }
    return e;
  }

  const submit = async () => {
    const local = validate();
    if (Object.keys(local).length) {
      setFieldErrors(local);
      setError('Please correct the highlighted fields.');
      // Take the reader to the first field that needs them rather than
      // leaving them to scroll.
      requestAnimationFrame(() => document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return;
    }
    setBusy(true); setError(null); setFieldErrors({});
    try {
      // One key for this attempt: a retry after a dropped connection replays
      // the stored response instead of adding the person twice.
      const created = await api.post<EmployeeDetail>('/employees', payload(d), idem.key());
      idem.rotate();
      if (shot) {
        try {
          await api.put(`/employees/${created.id}/photo`, { mimeType: shot.mimeType, content: shot.content });
        } catch (err) {
          // The person was added. Say what did not happen, and where to retry.
          toast('warning', `${d.fullName.trim()} was added, but the photo was not saved: ${errorMessage(err)} Add it from their record.`);
        }
      }
      nav(`/hr/employees/${created.id}`);
    } catch (err) {
      // The API returns RFC 9457 with per-field errors. Showing them against
      // the fields beats one banner saying "validation failed".
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-5">

      <div>
        <h1 className="text-h2 text-neutral-900">
          Add {d.personType === 'CANDIDATE' ? 'a candidate' : 'an employee'}
        </h1>
        <p className="mt-1 text-small text-neutral-500">
          To add a whole workforce,{' '}
          <Link to="/hr/employees/import" className="font-medium text-primary-600 hover:text-primary-700">
            import a CSV
          </Link>{' '}
          instead.
        </p>
      </div>

      {error && <Alert tone="danger" title="The employee was not created">{error}</Alert>}

      <Card>
        <CardHeader title="Personal" hint="Fields marked * are required." />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Record type" value={d.personType}
            onChange={(value) => setD((prev) => ({
              ...prev,
              personType: value as Draft['personType'],
              joinedOn: value === 'CANDIDATE' ? '' : prev.joinedOn,
            }))}
            className="sm:col-span-2"
            info={d.personType === 'CANDIDATE'
              ? 'For a pre-employment medical. This person is not counted as an employee until hired.'
              : 'For someone who has already joined the company.'}
          >
            <option value="EMPLOYEE">Employee</option>
            <option value="CANDIDATE">Pre-employment candidate</option>
          </SelectField>
          <TextField
            label="Employee ID" value={d.employeeCode} onChange={set('employeeCode')}
            required mono error={fieldErrors.employeeCode}
            info="Saved exactly as typed, with nothing added. Unique within the company; letters, digits, / - and _."
          />
          <TextField
            label="Full name" value={d.fullName} onChange={set('fullName')}
            required error={fieldErrors.fullName}
          />
          <SelectField label="Gender" value={d.sex} onChange={set('sex')} required error={fieldErrors.sex}>
            <option value="">Choose…</option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
          </SelectField>
          <DateField
            label="Date of birth" value={d.dateOfBirth} max={today} min="1900-01-01" required
            onChange={set('dateOfBirth')} error={fieldErrors.dateOfBirth}
            info="Used to pick the right reference range for their test results."
          />
          <TextField
            label="Mobile number" value={d.mobile} mono inputMode="numeric" required
            onChange={(v) => set('mobile')(v.replace(/\D/g, '').slice(0, 10))}
            error={fieldErrors.mobile} placeholder="10 digits"
            info="How they sign in to the employee app and receive appointment reminders."
          />
          <SearchSelect label="Blood group" value={d.bloodGroup} onChange={set('bloodGroup')} error={fieldErrors.bloodGroup}
            placeholder="Not recorded" searchPlaceholder="Search blood groups…"
            options={[{ value: '', label: 'Not recorded' }, ...BLOOD_GROUPS.map((g) => ({ value: g, label: g }))]} />
          {/* Kept, but out of the way: it decides the language of the
              worker's reminders and consent notice, and English is right for
              most. Open whenever it has an error or a non-default value. */}
          <details className="sm:col-span-2" open={Boolean(fieldErrors.preferredLocale || fieldErrors.email) || d.preferredLocale !== 'en' || Boolean(d.email)}>
            <summary className="flex min-h-10 cursor-pointer items-center text-small font-semibold text-neutral-700">
              More (email, language)
            </summary>
            <div className="mt-2 grid gap-4 sm:grid-cols-2">
              {/* Optional: most workers have none, so it waits here. */}
              <TextField
                label="Email (optional)" type="email" value={d.email} onChange={set('email')}
                error={fieldErrors.email}
              />
              <SelectField
                label="Preferred language" value={d.preferredLocale}
                onChange={set('preferredLocale')} error={fieldErrors.preferredLocale}
                info="The language their reminders and consent notice are written in."
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="gu">Gujarati</option>
              </SelectField>
            </div>
          </details>
        </CardBody>
      </Card>

      {/* Optional, and the one field on the form that is not typed: a file,
          or the camera on this device. Uploaded right after the record is
          created. */}
      <Card>
        <CardHeader title="Photo" hint="Shown on their record, the printed ID slip and in their app. Optional." />
        <CardBody className="flex flex-wrap items-center gap-4">
          <EmployeeAvatar name={d.fullName || '?'} src={shot?.previewUrl ?? null} size="xl" />
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" icon={<Camera className="h-4 w-4" />} onClick={() => setChoosingPhoto(true)}>
              {shot ? 'Change photo' : 'Add photo'}
            </Button>
            {shot && (
              <Button variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => { URL.revokeObjectURL(shot.previewUrl); setShot(null); }}>
                Remove
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Employment details (plant, designation, nature of work, type,
          joining date) are left to the edit screen: Add stays short (owner,
          29 Oct 2026). The plant is the one chosen in the header. */}

      {/* Held at the foot of the screen on a phone, so Add is in reach
          wherever in the form the reader is. */}
      <div className={cn('flex flex-wrap items-center justify-end gap-2 pb-4', STICKY_ACTIONS)}>
        {plant && <span className="mr-auto text-small text-neutral-500">Added to <strong className="font-medium text-neutral-700">{plant.name}</strong></span>}
        {fieldErrors.plantId && <span className="mr-auto text-small text-danger-600">No plant available. Ask {BRAND.name} support to set up your plant.</span>}
        <Button variant="ghost" onClick={() => nav('/hr/employees')}>Cancel</Button>
        {/* Never disabled for missing fields: a greyed-out button does not
            say which field is missing. Pressing it marks each one. */}
        <Button loading={busy} onClick={submit}>
          Add {d.personType === 'CANDIDATE' ? 'candidate' : 'employee'}
        </Button>
      </div>

      {choosingPhoto && (
        <PhotoDialog
          name={d.fullName.trim() || 'new employee'}
          hasPhoto={Boolean(shot)}
          onClose={() => setChoosingPhoto(false)}
          onPick={(next) => { setShot(next); setChoosingPhoto(false); }}
          onRemove={shot ? () => { setShot(null); setChoosingPhoto(false); } : undefined}
        />
      )}
    </div>
  );
}

/** YYYY-MM-DD in the browser's own timezone. */
function localDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * A date input with a range. `TextField` takes no `min`/`max`, and a date of
 * birth in the future is exactly the mistake the browser's own picker can
 * refuse before the form does.
 */
function DateField({ label, value, onChange, error, hint, info, min, max, required }: {
  label: string; value: string; onChange: (v: string) => void;
  error?: string; hint?: string; info?: string; min?: string; max?: string; required?: boolean;
}) {
  return (
    <Field label={label} error={error} hint={hint} info={info} required={required}>
      {(p) => (
        <Input {...p} type="date" value={value} min={min} max={max}
          onChange={(e) => onChange(e.target.value)} />
      )}
    </Field>
  );
}

export function EmployeeNew() {
  return <RequirePermission need="employee.create" title="Add an employee" what="Adding employees"><EmployeeNewPage /></RequirePermission>;
}
